import { useEffect, useMemo, useRef, type ReactNode } from "react";
import { LIBRARY } from "../design/copy.js";
import { coverSymbolFor } from "../design/covers.js";
import { environmentSymbols } from "../environment/section.js";
import { optimizeSvgDelivery } from "../environment/responsive-image.js";
import { goalLabel, skillLabel, trackLabel, type ShelfCase } from "../screens/library-shelves.js";

/**
 * THE FACE OF A CASE CARD, shared by the library's shelves and the coach's
 * front door (home pass, 2026-09-09): the cover plate, its sprite sheet, and
 * the three rows — track, goal, skill — in words. Lifted out of
 * screens/library.tsx unchanged so the two surfaces cannot drift.
 *
 * A CASE IS PAPER (2026-09-22, Andre; Codex's review applied). The large
 * case cards take the play desk's document card as their sibling — a heavier
 * one, with a painting: the same #fbfbfb paper at 92%, the same 1px edge and
 * offset shadow pair, square corners, straight (never the desk's tilt), a
 * kind row at the top with a case-file glyph across from it, the cover in a
 * hairline 4:3 frame on the section's paper, Museo at 700 for the client, the
 * three rows on the document card's rules, and the tension line as a byline.
 * The pieces live here (`CaseKindRow`, `CasePaperCover`, `CardRows`) and the
 * CSS in app.css under `.pc-case-*`, so the shelf and the desk stay one card.
 */
/**
 * Every cover the page needs, once, as <symbol> definitions the cards
 * reference through <use>. Zero width, zero height, out of the tree's flow
 * and the accessibility tree: it exists only to be pointed at.
 */
/** The case's cover painting: since 2026-09-23 the firm's people at work, for every case, with no room standing in. */
function coverArtFor(slug: string): string {
  return coverSymbolFor(slug);
}

export function CoverSprites({ slugs, ids: extraIds = [] }: { slugs: readonly string[]; /** Symbol ids that are not a case's cover: the focus and practice cards' paintings (2026-09-23). */ ids?: readonly string[] }): ReactNode {
  const node = useRef<SVGSVGElement>(null);
  const defs = useMemo(() => {
    const symbols = environmentSymbols();
    const ids = [...new Set([...slugs.map(coverArtFor), ...extraIds])];
    // A cover is a few hundred pixels wide on the shelf and the front door; the 768-wide
    // variant is plenty, and the original PNG stays the fallback (Release A0, 2026-09-15).
    return ids
      .map((id) => symbols.get(id)?.markup ?? "")
      .filter((markup) => markup.length > 0)
      .map((markup) => optimizeSvgDelivery(markup, 768))
      .join("");
  }, [slugs, extraIds]);
  useEffect(() => {
    const root = node.current;
    if (root === null) return;
    const recover = (event: Event): void => {
      if (!(event.target instanceof Element)) return;
      const png = event.target.getAttribute("data-png-fallback");
      if (png === null) return;
      event.target.removeAttribute("data-png-fallback");
      event.target.setAttribute("href", png);
    };
    root.addEventListener("error", recover, true);
    return () => root.removeEventListener("error", recover, true);
  }, [defs]);
  if (defs.length === 0) return null;
  return (
    <svg
      ref={node}
      aria-hidden="true"
      focusable="false"
      style={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }}
      // Our own bundled symbol markup, the same the environment section
      // inlines — not remote content.
      dangerouslySetInnerHTML={{ __html: `<defs>${defs}</defs>` }}
    />
  );
}

/** The card's plate: one symbol, contained, in ink on the cover tone. */
export function CoverPlate({ slug, className = "" }: { slug: string; className?: string | undefined }): ReactNode {
  const id = coverArtFor(slug);
  const symbol = environmentSymbols().get(id);
  return (
    <div className={`pc-cover overflow-hidden rounded-card bg-cover-plate text-ink ${className}`} aria-hidden="true">
      {symbol === undefined ? null : (
        <svg viewBox={`0 0 ${String(symbol.meta.footprint.w)} ${String(symbol.meta.footprint.h)}`} preserveAspectRatio="xMidYMid meet" focusable="false">
          <use href={`#${id}`} x={0} y={0} width={symbol.meta.footprint.w} height={symbol.meta.footprint.h} />
        </svg>
      )}
    </div>
  );
}

/**
 * THE PAINTING ON THE PAPER: the same cover, in a hairline frame on the
 * section's paper, a true 4:3 with no inner padding, never cropped
 * (`preserveAspectRatio` keeps the architecture whole). The frame is the
 * quietest line on the card — silhouettes and architecture first, the
 * card's edge second, the picture frame last (Codex, 2026-09-22).
 */
export function CasePaperCover({ slug, className = "" }: { slug: string; className?: string | undefined }): ReactNode {
  return <SymbolCover id={coverArtFor(slug)} className={className} />;
}

/** The same hairline frame around any painting the bundle carries, by symbol id: the focus and practice cards' covers (2026-09-23). */
export function SymbolCover({ id, className = "" }: { id: string; className?: string | undefined }): ReactNode {
  const symbol = environmentSymbols().get(id);
  return (
    <div className={`pc-case-cover ${className}`} aria-hidden="true">
      {symbol === undefined ? null : (
        <svg viewBox={`0 0 ${String(symbol.meta.footprint.w)} ${String(symbol.meta.footprint.h)}`} preserveAspectRatio="xMidYMid meet" focusable="false">
          <use href={`#${id}`} x={0} y={0} width={symbol.meta.footprint.w} height={symbol.meta.footprint.h} />
        </svg>
      )}
    </div>
  );
}

/**
 * THE KIND ROW: what this paper is, in words, with a small glyph across from
 * it — the document card's own first line. `words` is already the sentence
 * ("case · calibration", "continue · conflict management", "your plan"); the
 * glyph names the object and never the track, so there is one case-file for
 * every case and the memo's page for the plan. Decoration for the eye:
 * `aria-hidden`, no title, nothing in the markup names an enum.
 */
export type CardGlyph = "case" | "plan" | "focus" | "practice" | "book" | "email";

/** The four objects a desk paper can be: a case file, the plan's memo, a focus's mark (a ring around a point), a practice's pencil (2026-09-23). */
const GLYPH_PATHS: Readonly<Record<CardGlyph, string>> = {
  case: "M1.5 13.5V3h4.5l1.5 2h7v8.5Z M1.5 6.5h13",
  plan: "M3.5 1.5h6l3 3v10h-9Z M9.5 1.5v3h3 M5.5 8h5 M5.5 10.5h5",
  focus: "M8 2.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11Z M8 6.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Z M8 .5v2 M8 13.5v2 M.5 8h2 M13.5 8h2",
  practice: "M2.5 13.5l.9-3.4 8.2-8.2 2.5 2.5-8.2 8.2Z M10.6 2.9l2.5 2.5 M2.5 13.5l3.4-.9",
  /** An open book: a finished engagement's casebook (2026-09-24). */
  book: "M8 3.5C6.5 2.5 4 2 1.5 2.5v10C4 12 6.5 12.5 8 13.5 9.5 12.5 12 12 14.5 12.5v-10C12 2 9.5 2.5 8 3.5Z M8 3.5v10",
  /** An envelope: the email choices card (2026-10-07). */
  email: "M1.5 3.5h13v9h-13Z M1.5 3.5L8 9l6.5-5.5",
};

export function CaseKindRow({ words, glyph = "case" }: { words: string; glyph?: CardGlyph }): ReactNode {
  return (
    <span className="pc-case-kind">
      <span>{words}</span>
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
        <path d={GLYPH_PATHS[glyph]} />
      </svg>
    </span>
  );
}

/** "case · calibration": the shelf card's kind words. */
export function caseKindWords(entry: ShelfCase): string {
  return `${LIBRARY.kindCase} · ${trackLabel(entry.track)}`;
}

/** The three rows under a title: label left, value right, a rule between — the document card's rules. */
export function CardRows({ entry, extra = [] }: { entry: ShelfCase; extra?: readonly (readonly [string, string])[] }): ReactNode {
  return <PaperRows rows={[[LIBRARY.cardTrack, trackLabel(entry.track)], [LIBRARY.cardGoal, goalLabel(entry.pressureContext)], [LIBRARY.cardSkill, skillLabel(entry.primaryDimension)], ...extra]} />;
}

/** Any rows on the document card's rules, in words: the focus and practice papers' (2026-09-23). */
export function PaperRows({ rows }: { rows: readonly (readonly [string, string])[] }): ReactNode {
  return (
    <dl className="pc-case-rows">
      {rows.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
